import { defineStore } from 'pinia'
import type {
  BundleInterface,
  ItineraryInterface,
  ItineraryService,
  PriceThresholdInterface,
  ServiceClassInterface,
  ServiceInterface,
  TagInterface, TripInterface, VersionInterface
} from '@/types/trips'
import type { SupplierInterface } from '@/types/suppliers'
import type { UserInterface } from '@/types/user'
import type { AppSettingsInterface, CountryInterface, DayOfWeekInterface, RegionInterface } from '@/types/app'
import type { CommentInterface, ContactInterface } from '@/types/contacts'
import type { FilterDescriptor } from '@progress/kendo-data-query'
import { FilterOperator } from '@progress/kendo-data-query'
import { useAppDataStore } from '@/stores/app'
import { labels } from '@/appdata/labels'
import { handleError, stripHtml } from '@/appdata/globals'
import { IntlService } from '@progress/kendo-vue-intl'
import { useAuthStore } from '@/stores/auth'

export const useTripBuilderStore = defineStore('tripBuilder', {
  state: () => ({
    selectedTab: 'cities' as string,
    filters: {
      stringPartial: null as string | null,
      isFinal: false as boolean,
      itinerary: [] as string[],
      owners: [] as UserInterface[],
      countries: [] as CountryInterface[],
      regions: [] as RegionInterface[],
      tags: [] as TagInterface[],
    },
    autocomplete: {
      tags: [] as TagInterface[],
      bundles: [] as BundleInterface[],
      services: [] as ServiceInterface[],
      suppliers: [] as SupplierInterface[],
      contacts: [] as ContactInterface[],
      users: [] as UserInterface[],
    },
    flags: {
      modal: null as string | null,
    },
    context: null as ItineraryService | null,
    comment: null as CommentInterface | null,
  }),
  getters: {
    predicates(state) {
      const filters: Array<FilterDescriptor> = [];
      if ((state.filters.stringPartial?.length ?? 0) > 0) {
        filters.push({ field: 'trip.proposalName', operator: FilterOperator.Contains, value: state.filters.stringPartial })
      }

      if (state.filters.owners.length > 0) {
        filters.push({ field: 'owner.uuid', operator: FilterOperator.EqualTo, value: state.filters.owners.map(item => item.uuid) })
      }

      if (state.filters.countries.length > 0) {
        filters.push({ field: 'country.uuid', operator: FilterOperator.EqualTo, value: state.filters.countries.map(item => item.uuid) })
      }

      if (state.filters.regions.length > 0) {
        filters.push({ field: 'region.uuid', operator: FilterOperator.EqualTo, value: state.filters.regions.map(item => item.uuid) })
      }

      if (state.filters.isFinal === true) {
        filters.push({ field: 'version.final', operator: FilterOperator.EqualTo, value: 1 })
      }

      return filters;
    },
    overrideDescription(state) {
      return (stripHtml(state.context?.description ?? null).trim().length > 0) || (state.context?.description === '<p></p>')
    },
    overrideInclusions(state) {
      return (stripHtml(state.context?.inclusions ?? null).trim().length > 0) || (state.context?.inclusions === '<p></p>')
    },
    overrideExclusions(state) {
      return (stripHtml(state.context?.exclusions ?? null).trim()?.length > 0) || (state.context?.exclusions === '<p></p>')
    },
  },
  actions: {
    hasFilter(type: string) {
      return this.filters.itinerary.indexOf(type) >= 0;
    },
    hasFinalizedVersion(trip: TripInterface) {
      return (trip?.versions?.filter((version: VersionInterface) => {
        return version.final;
      })?.length ?? 0) > 0
    },

    //----------------------------------------------------------------------

    servicePlaceholder(service: ItineraryService) {
      if (!service) return null;

      if (service.name) {
        return service.name + ' | ' + service.service?.name
      }

      return null;
    },

    //----------------------------------------------------------------------

    newTrip(): TripInterface {
      const kendo: IntlService = new IntlService('en-US');

      return {
        owner: useAuthStore().user ?? null,
        assistedBy: null,
        dateDeparture: kendo.formatDate(new Date().addDays(7), Date.prototype.TZ_AGNOSTIC_FORMAT),
        duration: 1,
        itinerary: [
          {
            dateScheduled: kendo.formatDate(new Date().addDays(7), Date.prototype.TZ_AGNOSTIC_FORMAT),
            location: {},
            services: [],
            version: null
          }
        ],
        proposalName: null,
        proposalTotal: null,
        proposalHeaderImage: null,
        titlePageImage: null,
        commissionOverride: null,
        editable: true,
        lock: null,
        status: {
          depositPaid: null,
        },
        flags: {
          excludePricing: false,
          whiteLabel: false,
        },
        invoiceNumber: null,
        invoiceDescription: null,
        dateAgentInvoice: null,
        customerDescription: null,
        currentVersion: useAppDataStore().generateNewRevision(null),
        agent: null,
        primaryContact: null,
        numberOfPassengers: 1,
        contacts: [],
        countries: [],
        versions: [],
        fees: [],
        accessExceptions: [],
      };
    },

    //----------------------------------------------------------------------

    newActivity(day: ItineraryInterface): ItineraryService|null {
      if (!day?.trip?.uuid) return null;

      return {
        bookingStatus: null,
        quantity: null,
        markup: null,
        trip: { uuid: day.trip.uuid },
        tripDay: {
          uuid: day.uuid,
          location: day.location,
          dateScheduled: day.dateScheduled,
          trip: { uuid: day.trip?.uuid },
        },
      }
    },

    //----------------------------------------------------------------------

    newTravelDay(trip: TripInterface, dateScheduled: Date): ItineraryInterface|null {
      const kendo: IntlService = new IntlService('en-US');

      return {
        trip: { uuid: trip?.uuid },
        dateScheduled: kendo.formatDate(dateScheduled, Date.prototype.TZ_AGNOSTIC_FORMAT),
        location: null,
        version: trip.currentVersion?.uuid ? trip.currentVersion : null,
        services: [],
      }
    },

    //----------------------------------------------------------------------

    async moveServiceTo(trip: TripInterface|null, service: ItineraryService, targetDay: ItineraryInterface) {
      if (!trip || !targetDay) return null;

      if (service.tripDay) {
        // console.debug("ActivitiesTab | Remove from previous tripDay | ", service?.service?.name, )
        trip?.itinerary?.forEach((previousDay: ItineraryInterface) => {
          if (Array.isArray(previousDay?.services)) {
            previousDay.services = previousDay.services.filter((item: ItineraryService) => {
              // Fall back to object identity for a service that has never been
              // saved, otherwise `undefined !== undefined` is false and every
              // other unsaved service gets dropped off its day too.
              return service?.uuid
                ? (item.uuid !== service.uuid)
                : (item !== service);
            })
          }
        })
      }

      const selectedDay = trip.itinerary?.find((item) => {
        // Same reasoning as above: an unsaved target day has no uuid, so match
        // it by identity instead of landing on the first unsaved day.
        return targetDay.uuid
          ? (item.uuid === targetDay.uuid)
          : (item === targetDay);
      });
      if (!selectedDay) return null;

      if (!Array.isArray(selectedDay?.services)) {
        selectedDay.services = [];
      }

      // console.debug("ActivitiesTab | ", selectedDay);
      service.tripDay = {
        uuid: selectedDay.uuid,
        dateScheduled: selectedDay.dateScheduled,
        location: selectedDay.location,
        trip: {
          uuid: trip?.uuid
        }
      };

      selectedDay?.services?.push(service);

      return selectedDay ?? null;
    },

    //----------------------------------------------------------------------

    updateServiceQuantity(service: ItineraryService, newQuantity: number|null, options?: { propagate?: boolean }) {
      const appData = useAppDataStore();
      if (!service) return;

      service.quantity = newQuantity;
      if ((service.quantity ?? 0) <= 0) {
        service.quantity = null;
      }

      if (service.service?.type?.label !== 'hotel' && service.service?.pricingType === 'per-group') {
        service.quantity = 1;
      }

      if (service.service) {
        appData.storeActivity(service)
          .then(() => {
            const propagate = options?.propagate ?? true;

            if (propagate && (service.parent ?? null) === null) {
              this.propagateQuantityToLinkedServices(service);
            }

          })
          .catch((error) => { handleError(error) });
      }
    },

    //----------------------------------------------------------------------

    propagateQuantityToLinkedServices(service: ItineraryService) {
      const appData = useAppDataStore();
      if (service.parent || !service.uuid) return;
      if ((service.service?.children?.length ?? 0) > 0) return;

      appData.trip?.itinerary?.forEach((day: ItineraryInterface) => {
        day.services
          ?.filter((s: ItineraryService) => s.parent === service.uuid)
          .forEach((linked: ItineraryService) => {
            this.updateServiceQuantity(linked, service.quantity ?? 0, { propagate: false });
          });
      });
    },

    //----------------------------------------------------------------------

    defaultClassification(classifications: Array<ServiceClassInterface>|null) {
      if (!Array.isArray(classifications)) return null;

      return classifications.filter((item: ServiceClassInterface) => {
        return item.default;
      }).at(-1) ?? null;
    },

    //----------------------------------------------------------------------

    serviceVolumeThresholdByQuantity(service: ServiceInterface|null, quantity: number, classification: ServiceClassInterface |null): PriceThresholdInterface | null {
      console.info("TripBuilderStore | serviceVolumeThresholdByQuantity");
      if (!(service?.currentPrice ?? service?.defaultPrice)) return null;

      // Get default classification if it's not set and we have classifications
      if ((classification === null) && ((service.classifications?.length ?? 0) > 0)) {
        classification = this.defaultClassification(service.classifications ?? []);
      }

      const threshold = (service.currentPrice ?? service.defaultPrice)?.thresholds?.filter((item: PriceThresholdInterface) => {
        if ((service.classifications ?? []).length === 0) return true;

        return (!item.classification) || (item.classification && (item.classification.uuid === classification?.uuid));
      })
      .filter((item: PriceThresholdInterface) => {
        // console.debug(item.minimumQuantity, quantity);
        return item.minimumQuantity <= quantity;
      }).at(-1);

      if (!threshold) return null;

      return threshold;
    },

    //----------------------------------------------------------------------

    dailyPriceInDollars(service: ServiceInterface|null, quantity: number, classification: ServiceClassInterface |null) {
      console.info("TripBuilderStore | dailyPriceInDollars");
      if (!service) return [];

      // Get default classification if it's not set and we have classifications
      if ((classification === null) && ((service.classifications?.length ?? 0) > 0)) {
        classification = this.defaultClassification(service.classifications ?? []);
      }

      const threshold = service.currentPrice?.thresholds?.filter((item: PriceThresholdInterface) => {
          if ((service.classifications ?? []).length === 0) return true;

          return (!item.classification) || (item.classification && (item.classification.uuid === classification?.uuid));
        })
        .filter((item: PriceThresholdInterface) => {
          // console.debug("TripBuilderStore | dailyPriceInDollars", item.minimumQuantity, quantity);
          return item.minimumQuantity <= quantity;
        })
      ;

      console.debug("TripBuilderStore | dailyPriceInDollars", threshold);
      const dailyPrices: Array<PriceThresholdInterface|undefined|null> = [];
      if (threshold) {
        [ 0, 1, 2, 3, 4, 5, 6 ].forEach((indicator) => {
          dailyPrices[indicator] = threshold
            .filter((item: PriceThresholdInterface) => {
              return (Array.isArray(item.daysValid) && item.daysValid.includes(indicator)) || (item.daysValid === null);
            })
            .sort((element1: PriceThresholdInterface, element2: PriceThresholdInterface) => {
              if (element1.minimumQuantity > element2.minimumQuantity) {
                return -1;
              }
              else if (element1.minimumQuantity < element2.minimumQuantity) {
                return 1;
              }

              return 0;
            })
            .sort((element1: PriceThresholdInterface, element2: PriceThresholdInterface) => {
              if (element1.minimumQuantity > element2.minimumQuantity) {
                return -1;
              }
              else if (element1.minimumQuantity < element2.minimumQuantity) {
                return 1;
              }

              return 0;
            }).at(0) ?? null;

          console.debug("TripBuilderStore | dailyPriceInDollars", dailyPrices[indicator]);
        });
      }

      console.debug("TripBuilderStore | dailyPriceInDollars", dailyPrices);
      return dailyPrices;
    },

    //----------------------------------------------------------------------

    // A net price of 0 is a real €0 price; only a null or missing one falls back to the catalog
    hasNetPrice(item?: ItineraryService|null): item is ItineraryService & { price_euro: number } {
      return (item?.price_euro !== null) && (item?.price_euro !== undefined);
    },

    //----------------------------------------------------------------------

    servicePriceInDollars(item: ItineraryService|null, settings: AppSettingsInterface): number | null {
      console.info("TripBuilderStore | servicePriceInDollars");
      if (!item) return 0;
      console.debug("TripBuilderStore | servicePriceInDollars", item.service?.name, item);

      if (item.price_usd && !item.price_euro) {
        return item.price_usd;
      }

      const store = useAppDataStore();

      const day = store.trip?.itinerary?.filter((element: ItineraryInterface) => {
        return element.uuid === (item?.tripDay?.uuid ?? item.uuid ?? null);
      }).at(-1);
      // console.debug("Trip Day:", day);
      let dayIndex = new Date(day?.dateScheduled ?? "")?.getDay();
      if (isNaN(dayIndex)) {
        dayIndex = 1;
      }
      console.debug(dayIndex);

      const lookupQuantity = item.quantity ?? store.trip?.contacts?.length ?? 1;
      const dailyPrices =
        this.dailyPriceInDollars(item.service ?? null, lookupQuantity, item.classification ?? null);

      const threshold = dailyPrices[dayIndex];
      if (!threshold && !this.hasNetPrice(item)) return null;

      //console.debug("Threshold:", threshold);
      if (this.hasNetPrice(item)) {
        const convertedPrice = item.price_euro * (settings.exchangeRate ?? 1)
        //console.debug("Converted from Euros:", convertedPrice, (threshold.exchangeRate ?? settings.exchangeRate ?? 1));

        return Math.round((convertedPrice + Number.EPSILON) * 100) / 100
      } else if (threshold) {
        if (threshold.price_usd && !item.price_euro) {
          // console.debug("Price in USD:", threshold.price_usd);
          return threshold.price_usd;
        }

        if (item.price_euro || threshold.price_euro) {
          const convertedPrice = (item.price_euro ?? threshold.price_euro ?? 0) * (threshold.exchangeRate ?? settings.exchangeRate ?? 1)
          //console.debug("Converted from Euros:", convertedPrice, (threshold.exchangeRate ?? settings.exchangeRate ?? 1));

          return Math.round((convertedPrice + Number.EPSILON) * 100) / 100
        }
      }

      return 0;
    },

    //----------------------------------------------------------------------

    servicePriceInEuro(item?: ItineraryService|null, settings?: AppSettingsInterface) {
      console.info("TripBuilderStore | servicePriceInEuro");
      //console.info("TripBuilderStore | servicePriceInEuro | ", item);
      if (!item) return 0;

      if (this.hasNetPrice(item)) {
        return item.price_euro;
      }

      const store = useAppDataStore();

      const day = store.trip?.itinerary?.filter((element: ItineraryInterface) => {
        return element.uuid === (item.tripDay?.uuid ?? item.uuid ?? null);
      }).at(-1);
      //console.debug("TripBuilderStore | servicePriceInEuro | Trip Day", day);
      const dayIndex = new Date(day?.dateScheduled ?? "").getDay();

      const lookupQuantity = item.quantity ?? store.trip?.numberOfPassengers ?? 1;
      const dailyPrices = this.dailyPriceInDollars(item.service  ?? null, lookupQuantity, item.classification ?? null);

      const threshold = dailyPrices[dayIndex];
      if (!threshold) return null;

      if (threshold) {
        if (threshold.price_euro) {
          console.debug("Price in Euros:", threshold.price_euro);
          return threshold.price_euro;
        }

        if (threshold.price_usd) {
          const convertedPrice = threshold.price_usd / (threshold.exchangeRate ?? settings?.exchangeRate ?? 1)
          // console.debug("Converted from Euros:", convertedPrice, (threshold.exchangeRate ?? settings.exchangeRate ?? 1));

          return Math.round((convertedPrice + Number.EPSILON) * 100) / 100
        }
      }

      return 0;
    },

    //----------------------------------------------------------------------

    defaultServicePrice(item: ServiceInterface|null|undefined, defaultQuantity: number, _settings: AppSettingsInterface) {
      console.info("TripBuilderStore | defaultServicePrice");
      if (!item) return null;

      return this.serviceVolumeThresholdByQuantity(item ?? null, defaultQuantity, null);
    },

    //----------------------------------------------------------------------

    dayIncluded(item: DayOfWeekInterface): boolean {
      return labels.daysOfWeek.filter((day) => {
        return day.value === item.value;
      }).length > 0;
    },

    //----------------------------------------------------------------------

    resetOptions(currentUser?: UserInterface | null, salesTeam?: UserInterface[]) {
      // console.debug("TripBuilder | resetOptions");

      const includedinSalesTeam = salesTeam?.filter((item: UserInterface) => {
        return item.uuid === currentUser?.uuid;
      }).length ?? 0 > 0;

      // console.debug(salesTeam, currentUser, includedinSalesTeam);

      const defaultOwners = [];
      if (currentUser && includedinSalesTeam) {
        defaultOwners.push(currentUser);
      }

      this.$patch({
        selectedTab: 'cities',
        filters: {
          stringPartial: null,
          itinerary: [],
          owners: defaultOwners,
          countries: [],
          regions: [],
          tags: [],
        },
        autocomplete: {
          services:[],
          suppliers: [],
          contacts: [],
        },
        flags: {
          modal: null,
        },
        context: null,
      })
    },
    closeModals() {
      this.$patch({
        flags: { modal: null },
        context: null
      });
    },
    clearFilters() {
      this.$patch({
        filters: {
          itinerary: [],
          owners: [],
          countries: [],
          regions: [],
          tags: [],
        },
        autocomplete: {
          services:[],
          bundles:[],
          suppliers: [],
        },
      })
    },
    clearTooltips() {
      const visibleTooltips = document.querySelectorAll("[role=tooltip]");
      [...visibleTooltips].map(element => element.remove())
    },
  }
})
